import React, { useState } from 'react';
import { AlertTriangle, FileText, ListX, Table } from 'lucide-react';
import { toast } from 'sonner';
import {
  exportInvalidEmailsCSV,
  exportInvalidEmailsExcel,
  listInvalidEmails,
} from '../../utils/api';
import { useT } from '../../i18n';
import EmailPanelDialog from './EmailPanelDialog';

/**
 * La liste derrière le compte.
 *
 * « 12 adresses invalides retirées » n'est pas quelque chose qu'on peut
 * contrôler, contester ou relire — c'est le défaut que le sprint 25 a nommé
 * pour les deux déduplications, resté entier sur l'opération dont l'erreur
 * supprime un client.
 *
 * Elle s'ouvre dans sa propre fenêtre, au-dessus du formulaire, parce qu'on la
 * lit et qu'on l'exporte : c'est un document, pas un réglage. La liste
 * affichée est plafonnée, **l'export ne l'est pas** — il existe pour être
 * vérifié dans un tableur.
 *
 * Ce qui est lu dépend de la normalisation, donc le panneau interroge le
 * serveur à chaque ouverture avec le réglage qui est à l'écran, et le nomme.
 */
const InvalidEmailPanel = ({ column, normalize }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const show = async () => {
    setOpen(true);
    setLoading(true);
    setReport(null);
    try {
      const result = await listInvalidEmails({ column, normalize });
      setReport(result.report);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('email.failed'));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{t('email.invalidOpen')}</p>
            <p className="text-xs text-muted-foreground">{t('email.invalidHelp')}</p>
          </div>
          <button
            type="button"
            onClick={show}
            disabled={!column || loading}
            className="btn-secondary whitespace-nowrap flex items-center gap-1.5"
            data-testid="email-invalid-open"
          >
            <ListX className="h-4 w-4" />
            {t('email.invalidOpen')}
          </button>
        </div>
      </div>

      {open && (
        <EmailPanelDialog
          title={t('email.invalidTitle', { column })}
          testId="email-invalid"
          onClose={() => setOpen(false)}
        >
              {loading && (
                <p className="text-sm text-muted-foreground" data-testid="email-invalid-loading">
                  {t('email.panelLoading')}
                </p>
              )}

              {report && report.addresses.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="email-invalid-none">
                  {t('email.invalidNone')}
                </p>
              )}

              {report && report.addresses.length > 0 && (
                <>
                  <p className="text-sm font-medium" data-testid="email-invalid-summary">
                    {t('email.invalidSummary', {
                      addresses: report.addresses_total,
                      rows: report.rows_total,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {normalize ? t('email.panelNormalized') : t('email.panelRaw')}
                  </p>

                  <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
                    {report.addresses.map((entry) => (
                      <div key={entry.value} className="flex items-baseline gap-3 px-3 py-2">
                        <span className="text-sm font-medium break-all flex-1 min-w-0">
                          {entry.value}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {t('email.junkRows', { count: entry.rows })}
                        </span>
                      </div>
                    ))}
                  </div>

                  {report.truncated && (
                    <p className="text-xs text-muted-foreground" data-testid="email-invalid-more">
                      {t('email.invalidMore', { shown: report.addresses.length })}
                    </p>
                  )}
                </>
              )}

              {report && report.missing_rows > 0 && (
                <div className="flex items-start gap-2.5 rounded-[var(--radius)] border border-amber-200 bg-amber-50 p-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-900" data-testid="email-invalid-missing">
                    {t('email.invalidMissing', { count: report.missing_rows })}
                  </p>
                </div>
              )}

              {report && report.rows_total > 0 && (
                <div className="border-t border-border pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">{t('email.panelExportNote')}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => exportInvalidEmailsCSV(column, normalize)}
                      className="btn-primary flex items-center gap-2"
                      data-testid="email-invalid-export-csv"
                    >
                      <FileText className="h-4 w-4" />
                      {t('export.csv')}
                    </button>
                    <button
                      type="button"
                      onClick={() => exportInvalidEmailsExcel(column, normalize)}
                      className="btn-primary flex items-center gap-2"
                      data-testid="email-invalid-export-excel"
                    >
                      <Table className="h-4 w-4" />
                      {t('export.excel')}
                    </button>
                  </div>
                </div>
              )}
        </EmailPanelDialog>
      )}
    </>
  );
};

export default InvalidEmailPanel;
